import React, { useRef, useState, useEffect } from "react";
import styles from "./ImageSearchModal.module.scss";
import { Upload, Check } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useAPI } from "@/Hooks";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import ImageSearchModalContent from "./ImageSearchModalContent";

// Define the localStorage key for image search
export const IMAGE_SEARCH_STORAGE_KEY = "imageSearchData";

interface ImageSearchModalProps {
  isOpen: boolean;
  onClose: () => void;
  position: { top: number; left: number; width: number };
}

const ImageSearchModal: React.FC<ImageSearchModalProps> = ({
  isOpen,
  onClose,
  position,
}) => {
  const [isUploading, setIsUploading] = useState(false);
  const [isUploaded, setIsUploaded] = useState(false);

  const [localIsOpen, setLocalIsOpen] = useState(false);

  // Sync the local open state with the prop
  useEffect(() => {
    if (isOpen) {
      setLocalIsOpen(true);
    }
  }, [isOpen]);

  // Handle actual closing after exit animation completes
  const handleExitComplete = () => {
    if (!localIsOpen) {
      onClose();
    }
  };

  // Calculate modal position
  const modalStyle = {
    position: "fixed" as const,
    top: `${position.top}px`,
    left: `${position.left}px`,
    transform: "translateX(-50%)",
    zIndex: 1000,
  };

  const handleModalClick = (e: React.MouseEvent) => {
    if (!isUploading && !isUploaded) {
      setLocalIsOpen(false);
    }
    e.stopPropagation();
  };

  return (
    <AnimatePresence onExitComplete={handleExitComplete}>
      {isOpen && localIsOpen && (
        <motion.div
          className={styles.modalOverlay}
          onClick={handleModalClick}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
        >
          <motion.div
            className={styles.modalContent}
            style={modalStyle}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            <ImageSearchModalContent
              setLocalIsOpen={setLocalIsOpen}
              setIsUploading={setIsUploading}
              setIsUploaded={setIsUploaded}
              isUploading={isUploading}
              isUploaded={isUploaded}
            />
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ImageSearchModal;
